import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import StreamCard from '../components/StreamCard';

interface Stream {
  id: string;
  title: string;
  description: string | null;
  thumbnailUrl: string | null;
  priceCents: number;
  isLive: boolean;
  scheduledAt: string | null;
  viewerCount: number;
}

const isUpcoming = (scheduledAt: string | null): boolean => {
  if (!scheduledAt) return false;
  return new Date(scheduledAt) > new Date();
};

export default function Home() {
  const [streams, setStreams] = useState<Stream[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchStreams();

    // Poll for updates every 15 seconds to get fresh thumbnail URLs and viewer counts
    const interval = setInterval(() => {
      fetchStreams();
    }, 15000);

    return () => clearInterval(interval);
  }, []);

  async function fetchStreams() {
    try {
      const res = await fetch('/api/streams');
      if (!res.ok) throw new Error('Failed to fetch streams');
      const data = await res.json();
      setStreams(data);
    } catch (err) {
      setError('Failed to load streams. Please try again later.');
    } finally {
      setLoading(false);
    }
  }

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="page">
      <div className="container">
        <div className="page-header">
          <h1 className="page-title">Live & Upcoming Events</h1>
          <p className="page-subtitle">
            Get exclusive access to premium livestreams
          </p>
        </div>

        {error && <div className="message message-error">{error}</div>}

        {(() => {
          const liveStreams = streams.filter(s => s.isLive);
          const upcomingStreams = streams
            .filter(s => !s.isLive && isUpcoming(s.scheduledAt))
            .sort((a, b) => new Date(a.scheduledAt!).getTime() - new Date(b.scheduledAt!).getTime());

          return (
            <>
              {/* Live Now Section */}
              {liveStreams.length > 0 && (
                <section style={{ marginBottom: '48px' }}>
                  <h2 className="section-heading">
                    <span className="live-indicator"></span>
                    Live Now
                  </h2>
                  <div className="grid grid-3">
                    {liveStreams.map(stream => (
                      <Link key={stream.id} to={`/stream/${stream.id}`} style={{ textDecoration: 'none' }}>
                        <StreamCard stream={stream} />
                      </Link>
                    ))}
                  </div>
                </section>
              )}

              {/* Coming Soon Section */}
              {upcomingStreams.length > 0 && (
                <section style={{ marginBottom: '48px' }}>
                  <h2 className="section-heading">Coming Soon</h2>
                  <div className="grid grid-3">
                    {upcomingStreams.map(stream => (
                      <Link key={stream.id} to={`/stream/${stream.id}`} style={{ textDecoration: 'none' }}>
                        <StreamCard stream={stream} />
                      </Link>
                    ))}
                  </div>
                </section>
              )}

              {/* Empty State */}
              {liveStreams.length === 0 && upcomingStreams.length === 0 && (
                <div className="center-card" style={{ maxWidth: '600px', margin: '0 auto' }}>
                  <h2>No Streams Available</h2>
                  <p>There are no live or upcoming streams right now. Check back soon!</p>
                </div>
              )}
            </>
          );
        })()}
      </div>
    </div>
  );
}
